import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useLabelStore } from '@/stores/labelStore'
import { useElementsStore } from '@/stores/elementsStore'
import { DPI } from '@/lib/utils/units'
import { LabelMargins } from '@/types'

interface NewLabelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const presets = [
  { name: 'Small (50×25mm)', width: 50, height: 25 },
  { name: 'Medium (100×50mm)', width: 100, height: 50 },
  { name: 'Large (100×100mm)', width: 100, height: 100 },
  { name: 'Shipping (100×150mm)', width: 100, height: 150 },
  { name: 'Custom', width: 0, height: 0 },
]

// Visual label preview component
// Concept: width/height = printable area (útil), margins = external gap/unprintable area AROUND the label
function LabelPreview({
  width,
  height,
  margins,
}: {
  width: number
  height: number
  margins: LabelMargins
}) {
  // Total physical size = printable area + margins
  const totalWidth = width + margins.left + margins.right
  const totalHeight = height + margins.top + margins.bottom

  // Scale to fit in preview area (max 180px)
  const maxSize = 180
  const scale = Math.min(maxSize / totalWidth, maxSize / totalHeight, 2)

  const displayTotalWidth = totalWidth * scale
  const displayTotalHeight = totalHeight * scale
  const displayWidth = width * scale
  const displayHeight = height * scale

  const marginLeft = margins.left * scale
  const marginRight = margins.right * scale
  const marginTop = margins.top * scale
  const marginBottom = margins.bottom * scale

  const hasMargins = margins.left > 0 || margins.right > 0 || margins.top > 0 || margins.bottom > 0

  // Hatched pattern style for margin areas
  const hatchedStyle = {
    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(239,68,68,0.2) 2px, rgba(239,68,68,0.2) 4px)',
    backgroundColor: 'rgba(239,68,68,0.05)',
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-xs text-muted-foreground">Preview</div>
      {/* Total physical area (including margins) */}
      <div
        className="relative border border-dashed border-gray-300"
        style={{
          width: displayTotalWidth,
          height: displayTotalHeight,
        }}
      >
        {/* Margin areas (hatched pattern) - external to printable area */}
        {margins.top > 0 && (
          <div
            className="absolute left-0 right-0 top-0"
            style={{ height: marginTop, ...hatchedStyle }}
          />
        )}
        {margins.bottom > 0 && (
          <div
            className="absolute left-0 right-0 bottom-0"
            style={{ height: marginBottom, ...hatchedStyle }}
          />
        )}
        {margins.left > 0 && (
          <div
            className="absolute left-0 top-0 bottom-0"
            style={{ width: marginLeft, ...hatchedStyle }}
          />
        )}
        {margins.right > 0 && (
          <div
            className="absolute right-0 top-0 bottom-0"
            style={{ width: marginRight, ...hatchedStyle }}
          />
        )}

        {/* Printable area (útil) - white, in the center */}
        <div
          className="absolute bg-white border-2 border-gray-400"
          style={{
            left: marginLeft,
            top: marginTop,
            width: displayWidth,
            height: displayHeight,
          }}
        />

        {/* Dimension labels for printable area */}
        <div
          className="absolute text-[10px] text-muted-foreground"
          style={{
            left: marginLeft + displayWidth / 2,
            top: marginTop - 14,
            transform: 'translateX(-50%)',
          }}
        >
          {width}mm
        </div>
        <div
          className="absolute text-[10px] text-muted-foreground -rotate-90"
          style={{
            left: marginLeft - 14,
            top: marginTop + displayHeight / 2,
            transform: 'translateY(-50%)',
          }}
        >
          {height}mm
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground text-center">
        {hasMargins ? (
          <>
            Total: {totalWidth.toFixed(1)}×{totalHeight.toFixed(1)}mm (with gaps)
          </>
        ) : (
          'No margins/gaps'
        )}
      </div>
    </div>
  )
}

export function NewLabelDialog({ open, onOpenChange }: NewLabelDialogProps) {
  const { setLabel } = useLabelStore()
  const { clearElements } = useElementsStore()

  const [name, setName] = useState('Untitled Label')
  const [width, setWidth] = useState(100)
  const [height, setHeight] = useState(50)
  const [dpi, setDpi] = useState<DPI>(203)
  const [preset, setPreset] = useState('Medium (100×50mm)')
  const [margins, setMargins] = useState<LabelMargins>({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  })

  const handlePresetChange = (value: string) => {
    setPreset(value)
    const selected = presets.find((p) => p.name === value)
    if (selected && selected.width > 0) {
      setWidth(selected.width)
      setHeight(selected.height)
    }
  }

  const handleMarginChange = (side: keyof LabelMargins, value: number) => {
    setMargins((prev) => ({ ...prev, [side]: Math.max(0, value) }))
  }

  const handleCreate = () => {
    setLabel({
      name,
      width,
      height,
      dpi,
      margins,
      printSettings: {
        printSpeed: 4,
        darkness: 15,
        mediaType: 'gap',
        gapLength: 3,
      },
    })
    clearElements()
    onOpenChange(false)
  }

  // Margins are always valid since they are external to the printable area
  const marginsValid = true

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>New Label</DialogTitle>
          <DialogDescription>
            Configure your label dimensions and margins.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[1fr_auto] gap-6 py-4">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Label Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label>Preset Size</Label>
              <Select value={preset} onValueChange={handlePresetChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {presets.map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="width">Width (mm)</Label>
                <Input
                  id="width"
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(parseFloat(e.target.value) || 50)}
                  min={10}
                  max={500}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="height">Height (mm)</Label>
                <Input
                  id="height"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(parseFloat(e.target.value) || 25)}
                  min={10}
                  max={500}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Printer DPI</Label>
              <Select
                value={dpi.toString()}
                onValueChange={(v) => setDpi(parseInt(v) as DPI)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="203">203 DPI (8 dots/mm)</SelectItem>
                  <SelectItem value="300">300 DPI (12 dots/mm)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Margins / Unprintable Area (mm)</Label>
              <div className="grid grid-cols-4 gap-2">
                <div className="grid gap-1">
                  <Label htmlFor="margin-left" className="text-xs text-muted-foreground">Left</Label>
                  <Input
                    id="margin-left"
                    type="number"
                    value={margins.left}
                    onChange={(e) => handleMarginChange('left', parseFloat(e.target.value) || 0)}
                    min={0}
                    max={width / 2}
                    step={0.5}
                    className="h-8"
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="margin-right" className="text-xs text-muted-foreground">Right</Label>
                  <Input
                    id="margin-right"
                    type="number"
                    value={margins.right}
                    onChange={(e) => handleMarginChange('right', parseFloat(e.target.value) || 0)}
                    min={0}
                    max={width / 2}
                    step={0.5}
                    className="h-8"
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="margin-top" className="text-xs text-muted-foreground">Top</Label>
                  <Input
                    id="margin-top"
                    type="number"
                    value={margins.top}
                    onChange={(e) => handleMarginChange('top', parseFloat(e.target.value) || 0)}
                    min={0}
                    max={height / 2}
                    step={0.5}
                    className="h-8"
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="margin-bottom" className="text-xs text-muted-foreground">Bottom</Label>
                  <Input
                    id="margin-bottom"
                    type="number"
                    value={margins.bottom}
                    onChange={(e) => handleMarginChange('bottom', parseFloat(e.target.value) || 0)}
                    min={0}
                    max={height / 2}
                    step={0.5}
                    className="h-8"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Visual preview */}
          <div className="flex items-center justify-center p-4 bg-muted/30 rounded-lg min-w-[200px]">
            <LabelPreview width={width} height={height} margins={margins} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!marginsValid}>
            Create Label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
